import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardBody, CardHeader, Col, Badge, Spinner, Button, Modal, ModalHeader, ModalBody, ModalFooter, Table, Row, Col as GridCol } from 'reactstrap';
import axios from 'axios';
import { useAuth } from '../../../Provider/AuthProvider';

interface Order {
    rowid: string;
    ref: string;
    date_commande: string;
    name: string;
    total_ht: string;
    total_ttc: string;
    fk_statut: string;
    client_email?: string;
    client_phone?: string;
    client_address?: string;
    billed?: string;
    openExpeditions?: number;
    draftExpeditions?: number;
    validatedExpeditions?: number;
    user_nom?: string;
}

interface OrderDetails {
    id: string;
    ref: string;
    client: string;
    clientid: string;
    total_ht: string;
    total_ttc: string;
    date_commande: number;
    date_livraison: string;
    status: string;
    type: string;
    commandepdf: string;
    resteapayer: number;
    thirdparty: {
        name: string;
        address?: string;
        email?: string;
        phone?: string;
        rowid: string;
    };
    lines: Array<{
        id: string;
        product_label: string;
        subprice: string;
        qty: string;
        tva_tx: string;
        total_ht: string;
        total_tva: string;
        total_ttc: string;
        remise_percent: string;
        product_type: string;
        fk_product: string;
        brand_id: string;
        brand: string;
        rowid: string;
        ref: string;
    }>;
}

const RecentOrders = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [recentOrders, setRecentOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [loadingOrderId, setLoadingOrderId] = useState<string | null>(null);
    
    // États pour le modal de détails
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<OrderDetails | null>(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    // Fonction pour obtenir le libellé et la couleur du statut
    const getStatusBadge = (statusCode: string) => {
        switch (statusCode) {
            case "-1":
                return { label: "Annulée", color: "danger" };
            case "0":
                return { label: "Brouillon", color: "warning" };
            case "1":
                return { label: "Validée", color: "success" };
            case "2":
                return { label: "En cours", color: "info" };
            case "3":
                return { label: "Livrée", color: "success" };
            case "4":
                return { label: "Processed", color: "primary" };
            default:
                return { label: "Inconnu", color: "secondary" };
        }
    };

    // Récupérer les commandes récentes via l'API
    const fetchRecentOrders = async () => {
        if (!user?.token || !user?.entity) return;

        setLoading(true);
        setError(null);

        try {
            const response: any = await axios.post(
                "https://api.apapiecesauto.fr/apiV2/",
                {
                    action: "orders",
                    entity: user.entity,
                    page: 1,
                    sort: '-date_commande',
                    limit: 10
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${user.token}`,
                    },
                }
            );

            const formattedOrders = (response.data || []).map((order: any) => ({
                ...order,
                client_email: order.email || "",
                client_phone: order.phone || "",
                client_address: order.address || "",
                openExpeditions: order.openExpeditions || 0,
                draftExpeditions: order.draftExpeditions || 0,
                validatedExpeditions: order.validatedExpeditions || 0,
            }));

            setRecentOrders(formattedOrders);
        } catch (err) {
            console.error("Erreur lors du chargement des commandes récentes:", err);
            setError("Impossible de charger les commandes récentes");
        } finally {
            setLoading(false);
        }
    };

    // Charger les détails d'une commande et ouvrir le modal
    const handleViewOrder = async (orderId: string) => {
        if (!user?.token || !user?.entity) return;

        setLoadingOrderId(orderId);
        setLoadingDetails(true);

        try {
            const response: any = await axios.post(
                "https://api.apapiecesauto.fr/apiV2/",
                {
                    action: "orderdetails",
                    entity: user.entity,
                    id: orderId
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${user.token}`,
                    },
                }
            );

            if (response.data) {
                setSelectedOrder(response.data);
                setModalOpen(true);
            }
        } catch (err) {
            console.error("Erreur lors du chargement des détails:", err);
            setError("Impossible de charger les détails de la commande");
        } finally {
            setLoadingOrderId(null);
            setLoadingDetails(false);
        }
    };

    // Fermer le modal
    const toggleModal = () => {
        setModalOpen(!modalOpen);
        if (!modalOpen) {
            setSelectedOrder(null);
        }
    };

    // Charger les commandes au montage du composant
    useEffect(() => {
        fetchRecentOrders();
    }, [user?.entity, user?.token]);

    // Rafraîchir les données
    const handleRefresh = () => {
        fetchRecentOrders();
    };

    // Exporter le rapport
    const handleExport = () => {
        console.log("Export des commandes récentes");
    };

    // Formatage du montant en euros
    const formatAmount = (amount: string | number) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR'
        }).format(typeof amount === 'string' ? parseFloat(amount || '0') : amount);
    };

    // Formatage de la date
    const formatDate = (timestamp: number) => {
        return new Date(timestamp * 1000).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Obtenir l'initiale du client
    const getClientInitial = (clientName: string) => {
        return clientName?.charAt(0).toUpperCase() || '?';
    };

    return (
        <>
            <Col xl={12}>
                <Card>
                    <CardHeader className="align-items-center d-flex">
                        <h4 className="card-title mb-0 flex-grow-1">
                            Dernières Commandes
                            {loading && <Spinner size="sm" className="ms-2" />}
                        </h4>
                        <div className="flex-shrink-0">
                            <Button
                                type="button"
                                color="soft-secondary"
                                size="sm"
                                className="me-2"
                                onClick={handleRefresh}
                                disabled={loading}
                            >
                                <i className="ri-refresh-line align-middle"></i> Actualiser
                            </Button>
                            <Button
                                type="button"
                                color="soft-secondary"
                                size="sm"
                                onClick={handleExport}
                                disabled={loading || recentOrders.length === 0}
                            >
                                <i className="ri-file-list-3-line align-middle"></i> Exporter Rapport
                            </Button>
                        </div>
                    </CardHeader>

                    <CardBody>
                        {error && (
                            <div className="alert alert-danger alert-dismissible fade show" role="alert">
                                <i className="ri-error-warning-line me-1"></i>
                                {error}
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setError(null)}
                                    aria-label="Close"
                                />
                            </div>
                        )}

                        {!loading && recentOrders.length === 0 && !error && (
                            <div className="text-center py-4">
                                <i className="ri-inbox-line fs-1 text-muted"></i>
                                <p className="text-muted mt-2">Aucune commande récente</p>
                            </div>
                        )}

                        <div className="table-responsive table-card">
                            <table className="table table-borderless table-centered align-middle table-nowrap mb-0">
                                <thead className="text-muted table-light">
                                    <tr>
                                        <th scope="col">N° Commande</th>
                                        <th scope="col">Client</th>
                                        <th scope="col">Montant</th>
                                        <th scope="col">Statut</th>
                                        <th scope="col">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentOrders.slice(0, 5).map((item) => {
                                        const status = getStatusBadge(item.fk_statut);
                                        const isBilled = item.billed === "1";
                                        
                                        return (
                                            <tr key={item.rowid}>
                                                <td>
                                                    <Button
                                                        color="link"
                                                        className="fw-medium text-reset p-0"
                                                        onClick={() => handleViewOrder(item.rowid)}
                                                        disabled={loadingOrderId === item.rowid}
                                                    >
                                                        {item.ref}
                                                        {loadingOrderId === item.rowid && (
                                                            <Spinner size="sm" className="ms-1" />
                                                        )}
                                                    </Button>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <div className="flex-shrink-0 me-2">
                                                            <div className="avatar-xs">
                                                                <div className="avatar-title bg-primary-subtle text-primary rounded-circle">
                                                                    {getClientInitial(item.name)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex-grow-1">
                                                            {item.name}
                                                            {item.client_email && (
                                                                <small className="d-block text-muted">
                                                                    {item.client_email}
                                                                </small>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                               
                                                <td>
                                                    <span className="text-primary fw-medium">
                                                        {formatAmount(item.total_ttc)}
                                                    </span>
                                                    <small className="d-block text-muted">
                                                        HT: {formatAmount(item.total_ht)}
                                                    </small>
                                                </td>
                                               
                                                <td>
                                                    <div className="d-flex align-items-center gap-1">
                                                        <Badge color={status.color}>
                                                            {status.label}
                                                        </Badge>
                                                        {isBilled && (
                                                            <i
                                                                className="ri-currency-fill text-success"
                                                                title="Facturée"
                                                                style={{ fontSize: "14px" }}
                                                            ></i>
                                                        )}
                                                        {(item.validatedExpeditions ?? 0) > 0 && (
                                                            <i
                                                                className="ri-checkbox-circle-fill text-info"
                                                                title={`${item.validatedExpeditions} expédition(s)`}
                                                                style={{ fontSize: "14px" }}
                                                            ></i>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <Button
                                                        color="primary"
                                                        size="sm"
                                                        onClick={() => handleViewOrder(item.rowid)}
                                                        disabled={loadingOrderId === item.rowid}
                                                    >
                                                        {loadingOrderId === item.rowid ? (
                                                            <Spinner size="sm" />
                                                        ) : (
                                                            <>
                                                                <i className="ri-eye-line me-1"></i>
                                                                Voir
                                                            </>
                                                        )}
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {recentOrders.length > 0 && (
                            <div className="text-center mt-3">
                                <Button
                                    color="link"
                                    onClick={() => navigate('/commandes')}
                                    className="text-primary"
                                >
                                    Voir toutes les commandes
                                    <i className="ri-arrow-right-line ms-1"></i>
                                </Button>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Col>

            {/* Modal des détails de commande */}
            <Modal isOpen={modalOpen} toggle={toggleModal} size="lg" className="modal-dialog-scrollable">
                <ModalHeader toggle={toggleModal}>
                    {loadingDetails ? (
                        "Chargement des détails..."
                    ) : (
                        <>Détails de la commande {selectedOrder?.ref}</>
                    )}
                </ModalHeader>
                <ModalBody>
                    {loadingDetails ? (
                        <div className="text-center py-4">
                            <Spinner />
                            <p className="mt-2">Chargement des détails de la commande...</p>
                        </div>
                    ) : selectedOrder && (
                        <>
                            {/* Informations générales */}
                            <Row className="mb-4">
                                <GridCol md={6}>
                                    <h6 className="text-muted mb-2">Informations commande</h6>
                                    <p className="mb-1"><strong>Référence:</strong> {selectedOrder.ref}</p>
                                    <p className="mb-1"><strong>Date:</strong> {formatDate(selectedOrder.date_commande)}</p>
                                    <p className="mb-1"><strong>Type:</strong> {selectedOrder.type}</p>
                                    <p className="mb-1">
                                        <strong>Statut:</strong>{' '}
                                        <Badge color={getStatusBadge(selectedOrder.status).color}>
                                            {getStatusBadge(selectedOrder.status).label}
                                        </Badge>
                                    </p>
                                </GridCol>
                                <GridCol md={6}>
                                    <h6 className="text-muted mb-2">Client</h6>
                                    <p className="mb-1"><strong>Nom:</strong> {selectedOrder.thirdparty.name}</p>
                                    {selectedOrder.thirdparty.email && (
                                        <p className="mb-1"><strong>Email:</strong> {selectedOrder.thirdparty.email}</p>
                                    )}
                                    {selectedOrder.thirdparty.phone && (
                                        <p className="mb-1"><strong>Téléphone:</strong> {selectedOrder.thirdparty.phone}</p>
                                    )}
                                    {selectedOrder.thirdparty.address && (
                                        <p className="mb-1"><strong>Adresse:</strong> {selectedOrder.thirdparty.address}</p>
                                    )}
                                </GridCol>
                            </Row>

                            {/* Lignes de commande */}
                            <h6 className="text-muted mb-3">Articles commandés</h6>
                            <div className="table-responsive">
                                <Table bordered hover size="sm">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Réf.</th>
                                            <th>Produit</th>
                                            <th className="text-end">Qté</th>
                                            <th className="text-end">Prix unit.</th>
                                            <th className="text-end">Remise</th>
                                            <th className="text-end">TVA</th>
                                            <th className="text-end">Total HT</th>
                                            <th className="text-end">Total TTC</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedOrder.lines.map((line) => (
                                            <tr key={line.id}>
                                                <td>{line.ref}</td>
                                                <td>
                                                    {line.product_label}
                                                    {line.brand && (
                                                        <small className="d-block text-muted">
                                                            Marque: {line.brand}
                                                        </small>
                                                    )}
                                                </td>
                                                <td className="text-end">{line.qty}</td>
                                                <td className="text-end">{formatAmount(line.subprice)}</td>
                                                <td className="text-end">{line.remise_percent}%</td>
                                                <td className="text-end">{line.tva_tx}%</td>
                                                <td className="text-end">{formatAmount(line.total_ht)}</td>
                                                <td className="text-end">{formatAmount(line.total_ttc)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    
                                </Table>
                            </div>

                            {/* Actions supplémentaires */}
                            {selectedOrder.commandepdf && (
                                <div className="mt-3">
                                    <Button
                                        color="primary"
                                        size="sm"
                                        tag="a"
                                        href={selectedOrder.commandepdf}
                                        target="_blank"
                                        className="me-2"
                                    >
                                        <i className="ri-file-pdf-line me-1"></i>
                                        Voir le PDF
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleModal}>
                        Fermer
                    </Button>
                    {selectedOrder && (
                        <Button
                            color="primary"
                            onClick={() => {
                                toggleModal();
                                navigate('/commandes', { 
                                    state: { 
                                        openOrderModal: true, 
                                        orderId: selectedOrder.id,
                                        orderDetails: selectedOrder 
                                    } 
                                });
                            }}
                        >
                            Voir dans la liste
                        </Button>
                    )}
                </ModalFooter>
            </Modal>
        </>
    );
};

export default RecentOrders;